import { CostType, Stage } from "./CaseConfiguration.ts";
import type { Timeline } from "./timeline.ts";

/** What the court assessed of what a party recovers. */
export type Assessment = {
  // the costs it approved, which the shares apply to, or the sum to recover
  readonly of: "costs" | "recovery";
  readonly amount: number;
};

/** What a party that won recovers of its costs from those that lost to it. */
export type RecoveryOrder = {
  // how the user enters the shares; they are stored per cost type either way
  readonly mode: "byType" | "oneShare";
  // 0-1 per cost type
  readonly shares: { readonly [type in CostType]?: number };
  // absent until it is known
  readonly assessment?: Assessment;
};

/** What the court awarded of a damage on a claim or an issue. */
export type Award = {
  readonly issueId: string;
  readonly damageId: string;
  readonly amount: number;
};

/** The interest the court awarded on a claim or an issue. */
export type InterestAward = {
  readonly issueId: string;
  readonly amount: number;
};

/** What the case predicted for the decisions of its judgment. */
export type Prediction = {
  // of the outcome with these decisions: 0 where the case ruled it out
  readonly probability: number;
  // per party, its mean net result over all outcomes
  readonly meanNetResult: { readonly [partyId: string]: number };
};

/** What a party that won could recover, before any share of it. */
export type RecoverableCosts = {
  readonly recipientId: string;
  // those that lost to it
  readonly payerIds: string[];
  readonly byType: { readonly [type in CostType]?: number };
};

/** What the case says of a set of decisions, for the dialog to start from. */
export type JudgmentPreview = Prediction & {
  readonly recoverable: RecoverableCosts[];
};

/**
 * What the court decided. It sits beside what the user entered of the case
 * and overwrites none of it: while it is in force the case is calculated as a
 * case with this one outcome (see judgmentOf). Its date is the end of Trial on
 * the timeline.
 */
export type Judgment = {
  // the lowest issues, under the ids the calculation decides them (see
  // OutcomeSummary.scenario)
  readonly scenario: { readonly won: string[]; readonly lost: string[] };
  readonly awards: Award[];
  readonly interest?: InterestAward[];
  readonly recoveries: { readonly [partyId: string]: RecoveryOrder };
  // set by the server
  readonly predicted?: Prediction;
};

/** What a party owes another under the judgment, of one kind. */
export type AwardDue = {
  // stands for the award: payer, recipient, and damage or cost recovery
  readonly key: string;
  readonly payerId: string;
  readonly recipientId: string;
  // the damage awarded; absent for a cost recovery
  readonly damage?: string;
  readonly amount: number;
};

/** What was actually paid on an award. */
export type Payment = Omit<AwardDue, "damage">;

/** How the case ended. Its date is the end of Enforcement on the timeline. */
export type Closing = {
  readonly payments: Payment[];
};

type Stated = {
  timeline?: Timeline;
  judgment?: Judgment;
  closing?: Closing;
};

const hasEnded = (timeline: Timeline | undefined, stage: Stage): boolean =>
  !!timeline?.stages?.[stage]?.ended;

/**
 * The judgment in force: only while Trial has ended, and not by a settlement,
 * which leaves Appeals unreached. Reopening Trial leaves it stored, out of
 * force, so that closing Trial again starts from it.
 */
export const judgmentOf = ({ timeline, judgment }: Stated) =>
  hasEnded(timeline, Stage.Trial) && !timeline?.stages?.[Stage.Appeals]?.settled
    ? judgment
    : undefined;

/** Where a case stands with its judgment. */
export type JudgmentStatus = "none" | "judged" | "closed";

export const judgmentStatusOf = (c: Stated): JudgmentStatus =>
  closingOf(c) ? "closed" : judgmentOf(c) ? "judged" : "none";

/** How the case ended, in force only while Enforcement has ended. */
export const closingOf = (c: Stated): Closing | undefined =>
  judgmentOf(c) && hasEnded(c.timeline, Stage.Enforcement)
    ? c.closing
    : undefined;
